import { countries } from "@/lib/countries";

/**
 * Input masks and validation for contact data. The rules mirror the backend
 * (ContactFieldRules) so the UI never accepts what the API would refuse.
 * Phones are stored in E.164 ("+5531999998888"); masks are only for display/typing.
 */

export const onlyDigits = (value: string | null | undefined): string => (value || "").replace(/\D/g, "");

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const isValidEmail = (value: string | null | undefined): boolean => {
  const v = (value || "").trim();
  return v.length > 0 && v.length <= 254 && EMAIL.test(v);
};

/** "(31) 99999-8888" / "(31) 3333-4444" for a Brazilian number without country code. */
function maskBrazilLocal(digits: string): string {
  const d = digits.slice(0, 11);
  if (!d) return "";
  if (d.length <= 2) return `(${d}`;
  const area = d.slice(0, 2);
  const rest = d.slice(2);
  if (rest.length <= 4) return `(${area}) ${rest}`;
  if (rest.length <= 8) return `(${area}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  return `(${area}) ${rest.slice(0, 5)}-${rest.slice(5, 9)}`;
}

/**
 * Mask applied while typing. Without "+": Brazilian number with area code.
 * With "+": international; "+55" keeps the Brazilian grouping.
 */
export function maskPhone(value: string | null | undefined): string {
  const raw = (value || "").trim();
  if (raw.startsWith("+")) {
    const d = onlyDigits(raw).slice(0, 15);
    if (d.startsWith("55")) return `+55 ${maskBrazilLocal(d.slice(2))}`.trimEnd();
    return `+${d}`;
  }
  return maskBrazilLocal(onlyDigits(raw).replace(/^0+/, ""));
}

/** E.164 or null when the value cannot be a phone number (same rule as the API). */
export function phoneToE164(value: string | null | undefined): string | null {
  const raw = (value || "").trim();
  if (!raw) return null;
  const digits = onlyDigits(raw);
  if (raw.startsWith("+")) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  const d = digits.replace(/^0+/, "");
  if (d.length === 10 || d.length === 11) return `+55${d}`;
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) return `+${d}`;
  return null;
}

export const isValidPhone = (value: string | null | undefined): boolean => phoneToE164(value) !== null;

/** Display form of a stored phone: "+55 (31) 99999-8888", "+14155550100". */
export function formatPhoneDisplay(stored: string | null | undefined): string {
  if (!stored) return "";
  const e164 = phoneToE164(stored);
  return e164 ? maskPhone(e164) : stored;
}

/** Country (ISO code) and national digits of a stored phone, for inputs that split them. */
export function splitPhone(stored: string | null | undefined): { country: string; national: string } | null {
  const e164 = phoneToE164(stored);
  if (!e164) return null;
  const digits = e164.slice(1);
  // Longest calling code first ("+1" must not swallow "+1 868" style codes).
  const byCode = countries
    .filter((c) => c.phoneCode)
    .map((c) => ({ code: c.code, dial: onlyDigits(c.phoneCode) }))
    .sort((a, b) => b.dial.length - a.dial.length);
  const match = byCode.find((c) => digits.startsWith(c.dial));
  if (!match) return { country: "", national: digits };
  return { country: match.code, national: digits.slice(match.dial.length) };
}

/** Brazilian CEP "00000-000". */
export function maskCep(value: string | null | undefined): string {
  const d = onlyDigits(value).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

export const isValidCep = (value: string | null | undefined): boolean => onlyDigits(value).length === 8;
